import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'

interface D3LinkAnalysisGraphProps {
  graph: any
  onNodeClick?: (node: any) => void
  height?: number
}

// Maltego-style color scheme for different entity types
const NODE_COLORS: Record<string, string> = {
  query: '#00ff00',        // Bright green - target
  source: '#ff4444',       // Red - breach/incident
  breach: '#ff4444',       // Red - breach
  person: '#ff8800',       // Orange - person
  email: '#00aaff',        // Blue - email
  phone: '#aa00ff',        // Purple - phone
  domain: '#00ffaa',       // Cyan - domain
  location: '#00ff00',     // Green - location
  ip: '#0088ff',           // Blue - IP address
  password: '#ff0000',     // Red - password
  document: '#ffaa00',     // Orange - document
  web_result: '#888888',   // Gray - web result
  default: '#666666'       // Default gray
}

// Node shapes/sizes based on type
const NODE_SIZES: Record<string, number> = {
  query: 20,
  source: 16,
  breach: 16,
  person: 14,
  email: 12,
  phone: 12,
  domain: 12,
  location: 12,
  ip: 12,
  password: 10,
  document: 12,
  web_result: 10,
  default: 10
}

// Link colors based on relation type
const LINK_COLORS: Record<string, string> = {
  compromised_in: '#ff4444',
  exposed_in: '#ff4444',
  exposed_person: '#ff8800',
  owned_by: '#ff8800',
  exposed_email: '#00aaff',
  exposed_phone: '#aa00ff',
  located_at: '#00ff00',
  exposed_location: '#00ff00',
  mentioned_in: '#888888',
  associated_with: '#0088ff',
  default: '#666666'
}

export function D3LinkAnalysisGraph({ graph, onNodeClick, height = 600 }: D3LinkAnalysisGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null)
  const [selectedNode, setSelectedNode] = useState<any>(null)
  const [hoveredNode, setHoveredNode] = useState<any>(null)
  const [hoveredLink, setHoveredLink] = useState<any>(null)

  // Get node color based on type
  const getNodeColor = (node: any): string => {
    const nodeType = node.type || ''
    const entityType = node.props?.entity_type || ''
    const breachName = node.props?.breach_name || node.props?.node_type === 'breach'
    
    if (nodeType === 'query') return NODE_COLORS.query
    if (nodeType === 'source' && breachName) return NODE_COLORS.breach
    if (entityType && NODE_COLORS[entityType]) return NODE_COLORS[entityType]
    if (NODE_COLORS[nodeType]) return NODE_COLORS[nodeType]
    return NODE_COLORS.default
  }

  // Get node size based on type
  const getNodeSize = (node: any): number => {
    const nodeType = node.type || ''
    const entityType = node.props?.entity_type || ''
    
    if (nodeType === 'query') return NODE_SIZES.query
    if (nodeType === 'source') return NODE_SIZES.source
    if (entityType && NODE_SIZES[entityType]) return NODE_SIZES[entityType]
    if (NODE_SIZES[nodeType]) return NODE_SIZES[nodeType]
    return NODE_SIZES.default
  }

  // Get link color based on relation
  const getLinkColor = (link: any): string => {
    const relation = link.relation || ''
    return LINK_COLORS[relation] || LINK_COLORS.default
  }

  // Initialize D3 force-directed graph
  useEffect(() => {
    if (!svgRef.current || !graph || !graph.nodes || graph.nodes.length === 0) return

    const svg = d3.select(svgRef.current)
    const width = containerRef.current?.clientWidth || 800
    const containerHeight = height

    // Clear previous content
    svg.selectAll('*').remove()

    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform.toString())
      })

    svg.call(zoom)

    // Create container for zoomable content
    const container = svg.append('g')

    // Prepare data
    const nodes = graph.nodes.map((node: any) => ({
      ...node,
      id: node.id,
      label: node.label || node.id,
      type: node.type || 'default',
      color: getNodeColor(node),
      size: getNodeSize(node),
      x: Math.random() * width,
      y: Math.random() * containerHeight
    }))

    const links = (graph.edges || []).map((edge: any) => ({
      ...edge,
      source: edge.source,
      target: edge.target,
      relation: edge.relation || 'connected',
      color: getLinkColor(edge)
    }))

    // Create force simulation
    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, containerHeight / 2))
      .force('collision', d3.forceCollide().radius((d: any) => d.size + 5))

    simulationRef.current = simulation

    // Create arrow markers for directed edges
    const defs = svg.append('defs')
    const markerColors = [...new Set(links.map((l: any) => l.color))]
    
    markerColors.forEach((color, i) => {
      defs.append('marker')
        .attr('id', `arrow-${i}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 15)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color as string)
    })

    // Create links
    const link = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', (d: any) => d.color)
      .attr('stroke-width', (d: any) => Math.max(1, (d.weight || 1) * 2))
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', (d: any) => {
        const colorIndex = markerColors.indexOf(d.color)
        return `url(#arrow-${colorIndex})`
      })
      .on('mouseover', (event, d: any) => {
        setHoveredLink(d)
        d3.select(event.currentTarget)
          .attr('stroke-width', Math.max(2, (d.weight || 1) * 3))
          .attr('stroke-opacity', 1)
      })
      .on('mouseout', (event) => {
        setHoveredLink(null)
        d3.select(event.currentTarget)
          .attr('stroke-width', (d: any) => Math.max(1, (d.weight || 1) * 2))
          .attr('stroke-opacity', 0.6)
      })

    // Create nodes
    const node = container.append('g')
      .attr('class', 'nodes')
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', (d: any) => d.size)
      .attr('fill', (d: any) => d.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .call(drag(simulation) as any)
      .on('click', (event, d: any) => {
        event.stopPropagation()
        setSelectedNode(d)
        if (onNodeClick) {
          onNodeClick(d)
        }
      })
      .on('mouseover', (event, d: any) => {
        setHoveredNode(d)
        d3.select(event.currentTarget)
          .attr('r', (d: any) => d.size * 1.3)
          .attr('stroke-width', 3)
      })
      .on('mouseout', (event) => {
        setHoveredNode(null)
        d3.select(event.currentTarget)
          .attr('r', (d: any) => d.size)
          .attr('stroke-width', 2)
      })

    // Add labels
    const labels = container.append('g')
      .attr('class', 'labels')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text((d: any) => {
        const label = d.label || d.id || 'Unknown'
        return label.length > 20 ? label.substring(0, 17) + '...' : label
      })
      .attr('font-size', '11px')
      .attr('fill', '#333')
      .attr('text-anchor', 'middle')
      .attr('dy', (d: any) => d.size + 15)
      .attr('pointer-events', 'none')
      .style('user-select', 'none')
      .style('font-weight', (d: any) => d.type === 'query' ? 'bold' : 'normal')

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)

      node
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y)

      labels
        .attr('x', (d: any) => d.x)
        .attr('y', (d: any) => d.y)
    })

    // Initial zoom to fit
    setTimeout(() => {
      const bounds = (container.node() as SVGGElement)?.getBBox()
      if (bounds) {
        const fullWidth = bounds.width
        const fullHeight = bounds.height
        const widthScale = width / fullWidth
        const heightScale = containerHeight / fullHeight
        const scale = Math.min(widthScale, heightScale, 1) * 0.9
        const translateX = (width - fullWidth * scale) / 2 - bounds.x * scale
        const translateY = (containerHeight - fullHeight * scale) / 2 - bounds.y * scale

        svg.transition()
          .duration(750)
          .call(
            zoom.transform as any,
            d3.zoomIdentity.translate(translateX, translateY).scale(scale)
          )
      }
    }, 1000)

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop()
      }
    }
  }, [graph, onNodeClick, height])

  // Drag behavior
  const drag = (simulation: d3.Simulation<d3.SimulationNodeDatum, undefined>) => {
    function dragstarted(event: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart()
      event.subject.fx = event.subject.x
      event.subject.fy = event.subject.y
    }

    function dragged(event: any) {
      event.subject.fx = event.x
      event.subject.fy = event.y
    }

    function dragended(event: any) {
      if (!event.active) simulation.alphaTarget(0)
      event.subject.fx = null
      event.subject.fy = null
    }

    return d3.drag<SVGCircleElement, any>()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended)
  }

  // Zoom controls
  const handleZoomIn = () => {
    if (!svgRef.current) return
    const svg = d3.select(svgRef.current)
    svg.transition().call(d3.zoom<SVGSVGElement, unknown>().scaleBy as any, 1.3)
  }

  const handleZoomOut = () => {
    if (!svgRef.current) return
    const svg = d3.select(svgRef.current)
    svg.transition().call(d3.zoom<SVGSVGElement, unknown>().scaleBy as any, 1 / 1.3)
  }

  const handleResetZoom = () => {
    if (!svgRef.current || !containerRef.current) return
    const svg = d3.select(svgRef.current)
    const width = containerRef.current.clientWidth || 800
    const containerHeight = height

    const container = svg.select('g')
    const bounds = (container.node() as SVGGElement)?.getBBox()
    
    if (bounds) {
      const fullWidth = bounds.width
      const fullHeight = bounds.height
      const widthScale = width / fullWidth
      const heightScale = containerHeight / fullHeight
      const scale = Math.min(widthScale, heightScale, 1) * 0.9
      const translateX = (width - fullWidth * scale) / 2 - bounds.x * scale
      const translateY = (containerHeight - fullHeight * scale) / 2 - bounds.y * scale

      svg.transition()
        .duration(750)
        .call(
          d3.zoom<SVGSVGElement, unknown>().transform as any,
          d3.zoomIdentity.translate(translateX, translateY).scale(scale)
        )
    }
  }

  if (!graph || !graph.nodes || graph.nodes.length === 0) {
    return (
      <div className="relative w-full h-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-2 text-slate-700 dark:text-slate-300">⚠️ No Graph Data Available</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Waiting for data to load...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <button
          onClick={handleZoomIn}
          className="px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 text-sm shadow-sm"
          title="Zoom In"
        >
          +
        </button>
        <button
          onClick={handleZoomOut}
          className="px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 text-sm shadow-sm"
          title="Zoom Out"
        >
          −
        </button>
        <button
          onClick={handleResetZoom}
          className="px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 text-sm shadow-sm"
          title="Reset Zoom"
        >
          Reset
        </button>
      </div>

      {/* Node Info Panel */}
      {(hoveredNode || selectedNode) && (
        <div className="absolute bottom-4 left-4 z-10 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-4 shadow-lg max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">
              {selectedNode ? 'Selected' : 'Hovered'} Node
            </h3>
            {selectedNode && (
              <button
                onClick={() => setSelectedNode(null)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                ×
              </button>
            )}
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-medium text-slate-600 dark:text-slate-400">Label:</span>{' '}
              <span className="text-slate-900 dark:text-slate-100">
                {(selectedNode || hoveredNode)?.label || (selectedNode || hoveredNode)?.id}
              </span>
            </div>
            <div>
              <span className="font-medium text-slate-600 dark:text-slate-400">Type:</span>{' '}
              <span className="text-slate-900 dark:text-slate-100">
                {(selectedNode || hoveredNode)?.type || 'unknown'}
              </span>
            </div>
            {(selectedNode || hoveredNode)?.props && (
              <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {Object.entries((selectedNode || hoveredNode).props)
                    .slice(0, 3)
                    .map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium">{key}:</span> {String(value)}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Link Info */}
      {hoveredLink && (
        <div className="absolute top-16 right-2 z-10 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-3 shadow-lg">
          <div className="text-sm">
            <div className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Relation</div>
            <div className="text-slate-600 dark:text-slate-400">{hoveredLink.relation}</div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-10 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-3 shadow-lg">
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Node Types</div>
        <div className="space-y-1 text-xs">
          {Object.entries(NODE_COLORS).slice(0, 8).map(([type, color]) => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full border border-white dark:border-slate-700"
                style={{ backgroundColor: color }}
              />
              <span className="text-slate-600 dark:text-slate-400 capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SVG Container */}
      <div ref={containerRef} className="w-full" style={{ height: `${height}px` }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          className="cursor-move"
          style={{ background: '#0a0a0a' }}
        />
      </div>
    </div>
  )
}

